import { Conversation, ConversationType } from "@/types/conversation";
import {
  MessageType,
  RawMessageType,
  SendMessagePayload,
} from "@/types/message";

import { GroupMember } from "@/types/users";
import { WEBMSG_ENDPOINT } from "@/constants/links";
import api from "./api";
import { isValidJSON } from "@/utils/utils";
import { processIncomingMessage, ProcessedMessage } from "@/utils/encryptionProcessor";
import { isEncryptedMessage, isGroupMessageWireFormat } from "@/utils/crypto";
import {
  isGroupKeyDistributionMessage,
  isGroupKeyRequestMessage,
  parseGroupKeyRequestMessage,
  PendingKeyRequest,
} from "@/services/groupKeyService";
import { ensureFreshPublicKey } from "@/services/keyExchangeService";

/**
 * Decrypts a fetched history list and self-heals stale signing keys: a group message that
 * decrypts but fails signature auth means we hold a stale key for the sender (new-device
 * login). Collect those senders, fetch fresh keys once, and re-decrypt just those messages
 * rather than leaving "[auth failed]" until a live message arrives.
 */
async function decryptHistoryMessages(
  formatted: MessageType[],
  currentUserId: number,
  peerAddress: string | undefined
): Promise<MessageType[]> {
  // Process received messages and any sent messages that are encrypted, plus own outgoing
  // key-exchange JSON so it gets tagged isKeyExchange and stripped from the chat bubble.
  const shouldProcess = (msg: MessageType) =>
    msg.direction === "received" ||
    Number(msg.userId) !== Number(currentUserId) ||
    isEncryptedMessage(msg.message) ||
    isGroupMessageWireFormat(msg.message) ||
    isGroupKeyRequestMessage(msg.message) ||
    isGroupKeyDistributionMessage(msg.message);

  const decryptOne = (msg: MessageType) =>
    processIncomingMessage(msg.message, currentUserId, peerAddress, msg.senderUsername, msg.keysVersion, true);

  const applyProcessed = (msg: MessageType, processed: ProcessedMessage): MessageType => ({
    ...msg,
    message: processed.decryptedMessage,
    isEncrypted: processed.isEncrypted,
    isKeyExchange: processed.isKeyExchange,
    ...(processed.senderUsername && { senderUsername: processed.senderUsername }),
  });

  // Pass 1: decrypt/verify every message into its final form, noting the indices whose
  // signature auth failed on a stale signing key so we can re-decrypt just those.
  const sendersNeedingKeyRefresh = new Set<string>();
  const staleIndices: number[] = [];
  const result = await Promise.all(
    formatted.map(async (msg, i) => {
      if (!shouldProcess(msg)) return msg;
      const processed = await decryptOne(msg);
      if (processed.needsKeyRefresh) {
        sendersNeedingKeyRefresh.add(processed.needsKeyRefresh);
        staleIndices.push(i);
      }
      return applyProcessed(msg, processed);
    })
  );

  // Common case: nothing stale — return the already-final list, no second pass.
  if (sendersNeedingKeyRefresh.size === 0) return result;

  // Fetch fresh signing keys for the stale senders. ensureFreshPublicKey is TTL-gated
  // (and catches its own errors), so a permanently-unverifiable message — one whose
  // signing key is gone from @keys, so the re-fetch can never fix it — doesn't re-hit
  // @keys on every conversation open; it refreshes at most once per freshness window.
  await Promise.all(
    [...sendersNeedingKeyRefresh].map((sender) =>
      ensureFreshPublicKey(sender, currentUserId)
    )
  );

  // Pass 2: re-decrypt ONLY the messages that failed auth, now with the fresh keys.
  await Promise.all(
    staleIndices.map(async (i) => {
      const processed = await decryptOne(formatted[i]);
      result[i] = applyProcessed(formatted[i], processed);
    })
  );

  return result;
}

/**
 * Sends a POST request to fetch a list of conversations from the server.
 * The response is then formatted into an array of `Conversation` objects.
 *
 * @returns {Promise<Conversation[]>} A promise that resolves to an array of formatted conversations.
 * @throws {Error} Throws an error if the request fails.
 */
export async function fetchConversations() {
  const payload = {
    cmd: "GetConversations",
    draw: 1,
    order: [{ column: 2, dir: "desc" }],
    start: 0,
    length: 25,
    search: { value: "", regex: false },
  };

  try {
    const response = await api.post(WEBMSG_ENDPOINT, payload);

    const formatted: Conversation[] = [];

    type RawConversationArray = [
      ConversationType, // type (has html tags in it)
      string, // fullname
      string, // updated
      number, // sent
      number, // recvd
      string, // username
      number, // userid
      number, // addressType
      string // groupname
    ];

    response.data.data.forEach((convo: RawConversationArray) => {
      formatted.push({
        type: convo[8] ? "Group" : "User",
        fullname: convo[1], // note that group fullname includes description
        updated: convo[2],
        address: convo[5],
        addressId: convo[6],
        addressType: convo[7],
        groupName: convo[8],
      });
    });
    return formatted;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

/**
 * Deletes a conversation thread based on the provided address.
 *
 * @param {string} address - The address of the user thread to be deleted.
 * @returns {Promise<any>} A promise that resolves with the response data from the server.
 * @throws {Error} Throws an error if the request fails.
 */
export async function deleteUserThread(address: string) {
  const payload = {
    cmd: "DeleteUserThread",
    address: address,
  };

  try {
    const response = await api.post(WEBMSG_ENDPOINT, payload);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

/**
 * Helper func that formats the messages returned by the backend in the data object
 * 
 * @param payload The payload to send to the backend containing the cmd + params to execute
 * @returns The formatted messages + the rest of response.data
 */
const getFormattedMessages = async (payload: Object) => {
  const response = await api.post(WEBMSG_ENDPOINT, payload);
  const formatted: MessageType[] = [];

  response.data.messages.forEach((rawmsg: RawMessageType & { sender_username?: string }) => {
    const msg: MessageType = {
      addressId: rawmsg.address,
      userId: rawmsg.userid,
      created: rawmsg.created,
      direction: rawmsg.direction,
      lat: rawmsg.lat,
      lng: rawmsg.lng,
      message: rawmsg.message,
      isEncrypted: false, // Default state
      isKeyExchange: false,
      msgId: rawmsg.msg_id,
      // Group messages: backend supplies the sender's username on received rows.
      // Empty string is normalized away so the UI doesn't render an empty header.
      ...(rawmsg.sender_username && { senderUsername: rawmsg.sender_username }),
      ...(rawmsg.is_satellite && { isSatellite: true }),
      ...(rawmsg.direction === 'received' && rawmsg.keys_version != null && { keysVersion: rawmsg.keys_version }),
    };

    formatted.push(msg);
  });

  return { ...response.data, formatted };
};

type FetchMessageResponse = {
  maxSmsgId: number;
  maxRmsgId: number;
  messages: MessageType[];
  // group_key_requests the peer sent us (owner answers these — see collectPendingKeyRequests).
  pendingKeyRequests: PendingKeyRequest[];
};

// A group_key_request only ever arrives as a 1:1 DM addressed to the group owner, so the
// conversation peer (with_addr) is the requester. We surface these so the owner can answer
// requests that landed while it was offline — the live WS path only fires when the owner is
// online at send time. Guarded by a length check so we never JSON.parse multi-MB attachments.
function collectPendingKeyRequests(
  raw: MessageType[],
  peerAddress: string
): PendingKeyRequest[] {
  const out: PendingKeyRequest[] = [];
  for (const msg of raw) {
    if (msg.direction !== "received" || typeof msg.msgId !== "number") continue;
    if (msg.message.length > 256) continue; // requests are tiny JSON; skip attachments cheaply
    const parsed = parseGroupKeyRequestMessage(msg.message);
    if (parsed) {
      out.push({ requesterUsername: peerAddress, groupId: parsed.group_id, msgId: msg.msgId });
    }
  }
  return out;
}

/**
 * Fetches messages from the server for a given conversation, and formats them into a MessageType
 * array.
 *
 * @param {string} with_addr - The recipient's address. Can be a JSON string (for groups).
 * @param {string | number} with_id - The recipient's id.
 * @param {string | number} with_type - The type of the conversation (user, group, etc.).
 * @returns {Promise<FetchMessageResponse>} A promise that resolves to an array of formatted messages + the max sent msg id.
 * @throws {Error} Throws an error if the request fails.
 */
export async function fetchMessages(
  with_addr: string,
  with_id: string | number,
  with_type: string | number,
  currentUserId?: number,
  max_smsg_id?: number,
  max_rmsg_id?: number,
  limit?: number | null
): Promise<FetchMessageResponse> {
  const payload = {
    cmd: "GetConversationMessages",
    with_id,
    with_type,
    with_addr: isValidJSON(with_addr) ? JSON.parse(with_addr) : with_addr,
    from_sl4w: true,
    ...(max_smsg_id != null && { max_smsg_id }),
    ...(max_rmsg_id != null && { max_rmsg_id }),
    // null = load all (PHP treats 0/missing as the 200 default)
    messages_to_show: limit ?? Number.MAX_SAFE_INTEGER,
  };

  try {
    const response = await getFormattedMessages(payload);

    // Collect from the raw list before decryption replaces request content with a placeholder.
    const pendingKeyRequests = collectPendingKeyRequests(response.formatted, with_addr);

    let messages = response.formatted;

    if (currentUserId) {
      messages = await decryptHistoryMessages(response.formatted, currentUserId, with_addr);
    }

    return {
      maxSmsgId: response.max_smsgid,
      maxRmsgId: response.max_rmsgid,
      messages,
      pendingKeyRequests,
    };
  } catch (error) {
    console.error(error);
    throw error;
  }
}

/**
 * Sends a user message to the server, optionally including a media file.
 *
 * @param {SendMessagePayload} payload - The message payload containing the message/file to send + metadata.
 * @returns {Promise<any>} A promise that resolves to the server's response data.
 * @throws {Error} Throws an error if the request fails.
 */
export async function sendUserMessage(payload: SendMessagePayload) {
  try {
    const response = await api.post(WEBMSG_ENDPOINT, payload);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

/**
 * Retrieves the member info of a group (whether they're an owner, username, first name, last name).
 *
 * @param {number} groupId The ID of the group being queried.
 * @returns {Promise<GroupMember[]>} A promise that resolves to an array representing the members of the group.
 * @throws {Error} Throws an error if the request fails.
 */
export async function getGroupMembers(groupId: number): Promise<GroupMember[]> {
  const payload = {
    cmd: "GetGroupMembers",
    groupId: groupId,
  };
  try {
    const response = await api.post(WEBMSG_ENDPOINT, payload);
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error(error);
    throw error;
  }
}
